package one.healthium.today.ui.calories_counter

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import ifaandroid.feature.today_v2.today_ui_common.generated.resources.Res
import ifaandroid.feature.today_v2.today_ui_common.generated.resources.ic_charm_lightning_bolt
import loco.GENERAL_CARBO
import loco.GENERAL_FAT
import loco.GENERAL_FIBRE
import loco.GENERAL_PROTEIN
import loco.GENERAL_TODAY
import one.healthium.base.utils.Formatters.formatCalories
import one.healthium.base.utils.Formatters.formatGrams
import one.healthium.loco.resolve
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_16_20
import one.healthium.ui.compose.theme.green_3
import one.healthium.ui.compose.theme.red_1
import one.healthium.ui.compose.theme.white
import one.healthium.ui.compose.theme.yellow
import one.healthium.ui.compose.theme.yellow_6
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource


@Composable
fun CaloriesCounterPCOS(
    modifier: Modifier = Modifier,
    model: CaloriesCounterUiModel?,
    fullSizeCaloriesProgress: Boolean = false,
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        CaloriesProgress(model?.caloriesGoal, fullSizeCaloriesProgress)
        Spacer(Modifier.height(12.dp))
        Row {
            Column {
                CarboProgress(model?.carbohydratesGoal)
                Spacer(Modifier.height(12.dp))
                FatProgress(model?.fatGoal)
            }
            Spacer(Modifier.width(38.dp))
            Column {
                ProteinProgress(model?.proteinGoal)
                Spacer(Modifier.height(12.dp))
                FibreProgress(model?.fiberGoal)
            }
        }
        Spacer(Modifier.height(12.dp))
    }
}

@Composable
private fun CaloriesProgress(goal: NutritionGoalUiModel?, fullSizeCaloriesProgress: Boolean) {
    if (fullSizeCaloriesProgress) {
        CaloriesProgress(goal)
    } else {
        NutritionalGoalItem(
            goal = goal,
            name = stringResource(loco.Res.string.GENERAL_TODAY),
            color = yellow_6,
            isCalories = true
        )
    }
}

@Composable
internal fun CaloriesProgress(goal: NutritionGoalUiModel?) {
    val animatedProgress by animateFloatAsState(
        targetValue = goal?.progress ?: 0f,
        animationSpec = tween(durationMillis = 400, delayMillis = 200),
    )

    Box(
        modifier = Modifier.fillMaxWidth(),
        contentAlignment = Alignment.Center,
    ) {
        CircularProgressIndicator(
            modifier = Modifier.size(184.dp),
            color = AppTheme.colors.tertiary,
            trackColor = gray_16_20,
            strokeWidth = 20.dp,
            strokeCap = StrokeCap.Round,
            progress = { animatedProgress },
        )
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Image(
                painter = painterResource(Res.drawable.ic_charm_lightning_bolt),
                contentDescription = null,
            )
            Spacer(Modifier.height(8.dp))
            Text(
                modifier = Modifier.testTag("calories_progress"),
                text = goal?.amount.formatCalories().resolve(),
                style = AppTheme.textStyles.poppins20,
                color = AppTheme.colors.textInverted100,
            )
            Text(
                text = stringResource(loco.Res.string.GENERAL_TODAY),
                style = AppTheme.textStyles.secondary_12_400,
                color = AppTheme.colors.textInverted100.copy(alpha = 0.75f),
            )
        }
    }
}

@Composable
private fun CarboProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(
        goal = goal,
        name = stringResource(loco.Res.string.GENERAL_CARBO),
        color = yellow
    )
}

@Composable
private fun ProteinProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(
        goal = goal,
        name = stringResource(loco.Res.string.GENERAL_PROTEIN),
        color = white
    )
}

@Composable
private fun FatProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(
        goal = goal,
        name = stringResource(loco.Res.string.GENERAL_FAT),
        color = green_3
    )
}

@Composable
private fun FibreProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(
        goal = goal,
        name = stringResource(loco.Res.string.GENERAL_FIBRE),
        color = red_1
    )
}

@Composable
private fun NutritionalGoalItem(
    goal: NutritionGoalUiModel?,
    name: String,
    color: Color,
    isCalories: Boolean = false,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
    ) {
        CircularProgressIndicator(
            modifier = Modifier.size(28.dp),
            color = color,
            trackColor = gray_16_20,
            strokeWidth = 4.dp,
            strokeCap = StrokeCap.Square,
            progress = { goal?.progress ?: 0f },
        )
        Spacer(Modifier.width(12.dp))
        Column {
            if (isCalories) {
                NutritionGoalCaloriesItemValue(goal)
                Spacer(Modifier.height(3.dp))
            } else {
                NutritionGoalItemValue(goal)
                Spacer(Modifier.height(6.dp))
            }
            Text(
                text = name,
                style = AppTheme.textStyles.secondary_12_400,
                color = AppTheme.colors.textInverted100.copy(alpha = 0.5f),
            )
        }
    }
}

@Composable
private fun NutritionGoalCaloriesItemValue(goal: NutritionGoalUiModel?) {
    Text(
        text = (goal?.amount ?: 0f).formatCalories().resolve(),
        style = AppTheme.textStyles.secondary_20_600,
        color = AppTheme.colors.textInverted100,
    )
}

@Composable
private fun NutritionGoalItemValue(goal: NutritionGoalUiModel?) {
    Text(
        text = (goal?.amount ?: 0f).formatGrams().resolve(),
        style = AppTheme.textStyles.secondary_17_400,
        color = AppTheme.colors.textInverted100,
    )
}
